import {
  DeliverablesResponse,
  EditProductRequest,
  ProductDetailsResponse,
  ProductFormData,
  ProductResponse,
  RegulatoryDocumentResponse,
  RegulatoryDocumentUploadItem,
} from '@/@types/Products.ts';
import type { FunnelData } from '@/pages/EditProduct/FunnelMap/utils/funnelTypes';
import { api } from './api';

async function getProducts({
  currentPage,
  query = '',
  status = '',
}: {
  currentPage?: number;
  query?: string;
  status?: string;
}): Promise<ProductResponse> {
  const params = new URLSearchParams();

  params.append('page', (currentPage ?? 1).toString());
  if (query) {
    params.append('q', query);
  }
  if (status !== 'all') {
    params.append('status', status);
  }

  const response = await api.get(`/v1/products?${params.toString()}`);

  return response.data;
}

async function getProductsOptions(query?: string): Promise<any> {
  const params = new URLSearchParams();

  // Adiciona a query opcional, se fornecida
  if (query) {
    params.append('q', query);
  }

  const response = await api.get(`/v1/products?${params.toString()}`);

  const products = response.data.data.map((product: any) => ({
    value: product.id,
    label: product.name,
  }));

  return products;
}

async function getAllProductsBasicOptions(
  query?: string,
  productType?: string,
  categoryType?: string | string[],
  options?: {
    forIntegration?: string;
    currentIntegration?: string;
  },
): Promise<any> {
  const params = new URLSearchParams();
  if (query) params.append('q', query);

  const resolvedCategoryType =
    categoryType !== undefined && categoryType !== null
      ? categoryType
      : productType;

  if (resolvedCategoryType !== undefined && resolvedCategoryType !== null) {
    if (Array.isArray(resolvedCategoryType)) {
      for (const type of resolvedCategoryType) {
        if (type) params.append('category_type[]', type);
      }
    } else if (resolvedCategoryType) {
      // Callers existentes (ex.: shipping) usam string única
      params.append('category_type', resolvedCategoryType);
    }
  }

  if (options?.forIntegration) {
    params.append('for_integration', options.forIntegration);
  }
  if (options?.currentIntegration) {
    params.append('current_integration', options.currentIntegration);
  }

  const response = await api.get(`/v1/products/basic?${params.toString()}`);
  const list = response.data?.data ?? response.data ?? [];
  const safeList = Array.isArray(list) ? list : [];

  return safeList
    .map((product: any) => ({
      value: product.id,
      label: product.name,
    }))
    .sort((a, b) =>
      String(a.label ?? '').localeCompare(String(b.label ?? ''), undefined, {
        sensitivity: 'base',
      }),
    );
}

async function getProductsUpSellOptions(
  productId: string,
  query?: string,
): Promise<any> {
  const params = new URLSearchParams();

  if (query) {
    params.append('q', query);
  }

  const response = await api.get(
    `/v1/products/${productId}/search?${params.toString()}`,
  );

  const products = response.data.map((product: any) => ({
    value: product.id,
    label: product.name,
  }));

  return products;
}

async function createProduct(data: ProductFormData): Promise<any> {
  return await api.post('/v1/products', data);
}

async function editProduct(data: EditProductRequest): Promise<any> {
  return await api.put(`/v1/products/${data.id}`, data);
}

async function getProductById(id: string): Promise<ProductDetailsResponse> {
  const response = await api.get(`/v1/products/${id}`);

  return response.data.data;
}

async function createDeliverables({
  productId,
  files,
}: {
  productId: string;
  files: File[];
}) {
  await api.post(
    `/v1/products/${productId}/deliverables`,
    {
      deliverables: files,
    },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
}
async function uploadProductImage({
  productId,
  image,
}: {
  productId: string;
  image: File;
}) {
  const response = await api.post(
    `/v1/products/${productId}/image`,
    {
      image: image,
    },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return response.data.data;
}

async function listDeliverables(
  productId: string,
): Promise<DeliverablesResponse[]> {
  const response = await api.get(`/v1/products/${productId}/deliverables`);

  return response.data.data;
}

async function deleteDeliverable({
  deliverableId,
  productId,
}: {
  productId: string;
  deliverableId: string;
}) {
  await api.delete(
    `/v1/products/${productId}/deliverables/${deliverableId}/delete`,
  );
}

async function listRegulatoryDocuments(
  productId: string,
): Promise<RegulatoryDocumentResponse[]> {
  const response = await api.get(
    `/v1/products/${productId}/regulatory-documents`,
  );
  return response.data?.data ?? response.data ?? [];
}

async function uploadRegulatoryDocuments({
  productId,
  documents,
}: {
  productId: string;
  documents: RegulatoryDocumentUploadItem[];
}) {
  const formData = new FormData();
  documents.forEach((doc, index) => {
    formData.append(`documents[${index}][file]`, doc.file);
    formData.append(`documents[${index}][document_name]`, doc.document_name);
    if (doc.document_type) {
      formData.append(`documents[${index}][document_type]`, doc.document_type);
    }
    if (doc.note?.trim()) {
      formData.append(`documents[${index}][note]`, doc.note.trim());
    }
  });

  await api.post(`/v1/products/${productId}/regulatory-documents`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

async function deleteRegulatoryDocument({
  productId,
  documentId,
}: {
  productId: string;
  documentId: string;
}) {
  await api.delete(
    `/v1/products/${productId}/regulatory-documents/${documentId}/delete`,
  );
}

async function duplicateProduct({
  productId,
  options,
}: {
  productId: string;
  options: any;
}) {
  const response = await api.post(`/v1/products/${productId}/replicate`, {
    ...options,
  });

  return response.data.data;
}

async function listCategories({ categoryType }: { categoryType: string }) {
  const response = await api.get(`/v1/categories/${categoryType}`);

  return response.data.data;
}

async function getCoproducers({
  currentPage,
  query = '',
}: {
  currentPage?: number;
  query?: string;
}): Promise<any> {
  const params = new URLSearchParams();

  params.append('page', (currentPage ?? 1).toString());
  if (query) {
    params.append('q', query);
  }

  const response = await api.get(
    `/v1/products/my-coproducers?${params.toString()}`,
  );

  return response.data;
}

async function getMyAffiliates({
  currentPage,
  query = '',
}: {
  currentPage?: number;
  query?: string;
}): Promise<any> {
  const params = new URLSearchParams();

  params.append('page', (currentPage ?? 1).toString());
  if (query) {
    params.append('q', query);
  }

  const response = await api.get(
    `/v1/products/my-affiliates?${params.toString()}`,
  );

  return response.data;
}

type ValidateSupportEmailOptions = {
  send?: boolean
  force?: boolean
}

async function validateSupportEmail(
  email: string,
  productName?: string,
  options: ValidateSupportEmailOptions = {},
): Promise<any> {
  return await api.post('/v1/product/validate-support-email', {
    email,
    product_name: productName,
    send: options.send ?? true,
    force: options.force ?? false,
  });
}

async function verifySupportEmailCode(
  email: string,
  code: string,
): Promise<any> {
  return await api.post('/v1/product/verify-support-email-code', {
    email,
    code,
  });
}

async function getFunnel(productId: string): Promise<FunnelData> {
  const response = await api.get(`/v1/products/${productId}/funnel`);
  return response.data;
}

export const ProductsService = {
  getProducts,
  createProduct,
  editProduct,
  getProductById,
  listDeliverables,
  createDeliverables,
  deleteDeliverable,
  listRegulatoryDocuments,
  uploadRegulatoryDocuments,
  deleteRegulatoryDocument,
  uploadProductImage,
  duplicateProduct,
  listCategories,
  getCoproducers,
  getMyAffiliates,
  getProductsOptions,
  getProductsUpSellOptions,
  getAllProductsBasicOptions,
  validateSupportEmail,
  verifySupportEmailCode,
  getFunnel,
};
